import React, { useState } from "react";
import { AnimatePresence } from "framer-motion";
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import { Button, Badge, ApiLoader } from "../../ui";
import FrameworkFormModal from "./FrameworkFormModal";
import ConfirmDialog from "./ConfirmDialog";
import UploadFileModal from "../../ui/UploadFileModal";
import { exportFrameworkMasterApi, importFrameworkMasterApi } from "../../../services/axios/AuthApis/TrustCenterApis";
import { toast } from "../../../utils/notify";

/**
 * Framework list view with search, type filter, and CRUD actions.
 *
 * Props:
 *   frameworks       – array of FrameworkMaster objects
 *   loading          – boolean
 *   onOpenFramework  – (fw) => void   (drill into controls)
 *   onSave           – (form) => Promise<void>
 *   onDelete         – (id) => Promise<void>
 */
const FrameworkTable = ({
  frameworks,
  loading,
  canManageFrameworks = true,
  onOpenFramework,
  onSave,
  onDelete,
  userRole = '',
}) => {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [modal, setModal] = useState({ open: false, data: null });
  const [confirm, setConfirm] = useState({ open: false, id: null, name: "" });
  const [exportLoading, setExportLoading] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);

  const uniqueTypes = [...new Set(frameworks.map((f) => f.framework_type).filter(Boolean))].sort();

  const filtered = frameworks.filter((fw) => {
    const name = String(fw?.name || "");
    const country = String(fw?.country_or_origin || "");
    const frameworkType = String(fw?.framework_type || "");
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      name.toLowerCase().includes(q) ||
      country.toLowerCase().includes(q) ||
      frameworkType.toLowerCase().includes(q);
    const matchType = !typeFilter || frameworkType === typeFilter;
    return matchSearch && matchType;
  });

  const handleDelete = async () => {
    await onDelete(confirm.id);
    setConfirm({ open: false, id: null, name: "" });
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      await exportFrameworkMasterApi();
      toast.success("Framework data exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export framework data");
    } finally {
      setExportLoading(false);
    }
  };

  const handleUploadFile = async () => {
    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }

    try {
      setImportLoading(true);
      const result = await importFrameworkMasterApi(selectedFile);

      // Show success summary
      if (result.summary) {
        const { created_frameworks, reused_frameworks, created_controls, skipped_controls } = result.summary;
        toast.success(
          `Import successful!\n` +
          `Created: ${created_frameworks} frameworks, ${created_controls} controls\n` +
          `Reused: ${reused_frameworks} frameworks, Skipped: ${skipped_controls} controls`
        );
      }

      // Show errors if any
      if (result.errors && result.errors.length > 0) {
        toast.error(`Import completed with ${result.errors.length} errors`);
      }

      // Close modal and reset
      setUploadModal(false);
      setSelectedFile(null);
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.message || "Failed to import framework data");
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <div>
      {/* header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheckIcon className="w-6 h-6" style={{ color: "var(--app-primary-color, #26256b)" }} />
            Frameworks
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage the global catalog of cybersecurity &amp; compliance frameworks
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="primary"
            size="sm"
            icon={ArrowDownTrayIcon}
            onClick={handleExport}
            disabled={exportLoading || frameworks.length === 0}
          >
            {exportLoading ? "Exporting..." : "Export"}
          </Button>
          {/* {canManageFrameworks && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              icon={ArrowUpTrayIcon}
              onClick={() => setUploadModal(true)}
              disabled={importLoading}
            >
              {importLoading ? "Importing..." : "Import"}
            </Button>
          )} */}
          {canManageFrameworks && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              icon={PlusIcon}
              onClick={() => setModal({ open: true, data: null })}
            >
              New Framework
            </Button>
          )}
        </div>
      </div>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search frameworks…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">All types</option>
          {uniqueTypes.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <span className="text-sm text-gray-500">{filtered.length} frameworks</span>
      </div>

      {/* content */}
      {loading ? (
        <div className="flex justify-center py-20">
          <ApiLoader label="Loading frameworks…" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <DocumentTextIcon className="w-14 h-14 mb-3 opacity-40" />
          <p className="text-lg font-medium">No frameworks found</p>
          <p className="text-sm mt-1">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((fw) => {
            const isDisabledByEnabled = !canManageFrameworks && fw.enabled === false;
            return (
              <div
                key={fw.id}
                onClick={() => !isDisabledByEnabled && onOpenFramework(fw)}
                className={`relative rounded-xl border shadow-sm transition-all duration-200 flex flex-col ${isDisabledByEnabled
                  ? "bg-gray-100 border-gray-300 opacity-75"
                  : "bg-white border-gray-200 hover:shadow-md hover:border-primary-200 cursor-pointer"
                  }`}
              >
                {/* lock overlay for disabled cards */}
                {isDisabledByEnabled && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center rounded-xl bg-gray-100/90 backdrop-blur-[2px] pointer-events-none z-10 px-4 text-center">
                    <p className="text-sm font-bold text-gray-800 line-clamp-1 w-full mb-1">{fw.name}</p>
                    {fw.description && (
                      <p className="text-xs text-gray-500 line-clamp-2 w-full mb-3 leading-relaxed">{fw.description}</p>
                    )}
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center shadow-inner mb-2">
                      <LockClosedIcon className="w-5 h-5 text-gray-500" />
                    </div>
                    <p className="text-xs font-semibold text-gray-700">Framework Locked</p>
                    <p className="text-xs text-gray-500 leading-relaxed mt-0.5">
                      To unlock, contact the <span className="font-medium text-gray-700">Tanikkai's Team</span>
                    </p>
                  </div>
                )}

                {/* card header */}
                <div className="p-4 flex-1">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => onOpenFramework(fw)}
                      className={`font-semibold text-left leading-snug line-clamp-2 transition-colors ${isDisabledByEnabled
                        ? "text-gray-600"
                        : "text-gray-900 hover:text-primary-600"
                        }`}
                    >
                      {fw.name}
                    </button>
                    {isDisabledByEnabled ? (
                      <Badge variant="secondary" size="sm">Disabled</Badge>
                    ) : fw.is_active !== false ? (
                      <Badge variant="success" size="sm" icon={CheckCircleIcon}>Active</Badge>
                    ) : (
                      <Badge variant="secondary" size="sm">Inactive</Badge>
                    )}
                  </div>

                  {fw.description && (
                    <p className="text-xs text-gray-500 line-clamp-2 mb-3 leading-relaxed">
                      {fw.description}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-1.5 mb-3">
                    <Badge variant="primary" size="sm">{fw.framework_type}</Badge>
                    {fw.country_or_origin && (
                      <Badge variant="secondary" size="sm">{fw.country_or_origin}</Badge>
                    )}
                  </div>

                  {/* Acknowledgement progress bar — customer_admin / security_analyst only */}
                  {(userRole === 'customer_admin' || userRole === 'security_analyst') && fw.enabled && fw.controls_count > 0 && (() => {
                    const pct = Math.min(100, Math.round((fw.acknowledged_count / fw.controls_count) * 100));
                    return (
                      <div className="mt-1 mb-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] text-gray-500">Acknowledgement</span>
                          <span className="text-[10px] font-semibold text-gray-700">
                            {fw.acknowledged_count}/{fw.controls_count} controls ({pct}%)
                          </span>
                        </div>
                        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${pct === 100 ? 'bg-green-500' : pct > 0 ? 'bg-blue-500' : 'bg-gray-300'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* card footer */}
                <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => onOpenFramework(fw)}
                    className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-primary-600 transition-colors"
                  >
                    <DocumentTextIcon className="w-3.5 h-3.5" />
                    {fw.controls_count} controls{fw.enabled ? ` (${Number(fw.acknowledge_average || 0).toFixed(2)}% ack)` : ""}
                  </button>
                  {canManageFrameworks && userRole !== 'auditor' && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        title="Edit"
                        onClick={() => setModal({ open: true, data: fw })}
                        className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        title="Delete"
                        onClick={() => setConfirm({ open: true, id: fw.id, name: fw.name })}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* modals */}
      {canManageFrameworks && (
        <AnimatePresence>
          {modal.open && (
            <FrameworkFormModal
              open={modal.open}
              initial={modal.data}
              onSave={onSave}
              onClose={() => setModal({ open: false, data: null })}
            />
          )}
        </AnimatePresence>
      )}

      {canManageFrameworks && (
        <AnimatePresence>
          {confirm.open && (
            <ConfirmDialog
              open={confirm.open}
              title="Delete Framework"
              message={`Are you sure you want to delete "${confirm.name}"? All associated controls will also be removed.`}
              onConfirm={handleDelete}
              onCancel={() => setConfirm({ open: false, id: null, name: "" })}
            />
          )}
        </AnimatePresence>
      )}
      {/* Upload Modal for Framework Import */}
      <UploadFileModal
        showUploadFile={uploadModal}
        setShowUploadFile={setUploadModal}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        handleUploadFile={handleUploadFile}
        title="Import Framework Master Data"
        subtitle="Upload an Excel file with framework and control data"
        acceptedFileTypes=".xlsx,.xls"
        maxFileSize={10}
        fileTypeLabel={["XLSX", "XLS"]}
      />
    </div>
  );
};

export default FrameworkTable;
